import { useState, useRef, useEffect } from "react";
import "../css/Cities.css";

const CLOUD_NAME = "dnriybfwk";
const UPLOAD_PRESET = "ofak_assets";
const CITIES_API = "/api/cities";
const STATES_API = "/api/states";

// ─── Cities Tab ──────────────────────────────────────────────────────────────

function CitiesTab() {
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ name: "", state: "", cover: null });
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState(null);
  const fileRef = useRef();

  const fetchCities = async () => {
    try {
      const res = await fetch(CITIES_API);
      const data = await res.json();
      if (data.success) {
        setCities(
          data.data.map((item) => ({
            id: item.id,
            name: item.city,
            state: item.state,
            cover: item.image,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => { fetchCities(); }, []);

  const handleInput = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((err) => ({ ...err, [e.target.name]: "" }));
  };

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setForm((f) => ({ ...f, cover: file }));
    setPreview(URL.createObjectURL(file));
    setErrors((err) => ({ ...err, cover: "" }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "City name is required.";
    if (!form.state.trim()) e.state = "State is required.";
    if (!editId && !form.cover) e.cover = "Cover image is required.";
    return e;
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ name: "", state: "", cover: null });
    setPreview(null);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (city) => {
    setEditId(city.id);
    setForm({ name: city.name, state: city.state, cover: "__existing__" });
    setPreview(city.cover);
    setErrors({});
    setShowModal(true);
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    try {
      let imageUrl = preview;

      if (form.cover && form.cover !== "__existing__") {
        const cloudFormData = new FormData();
        cloudFormData.append("file", form.cover);
        cloudFormData.append("upload_preset", UPLOAD_PRESET);
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
          { method: "POST", body: cloudFormData }
        );
        const cloudinaryData = await cloudinaryRes.json();
        imageUrl = cloudinaryData.secure_url;
      }

      const payload = { city: form.name.trim(), state: form.state.trim(), image: imageUrl };

      if (editId) {
        const res = await fetch(`${CITIES_API}/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) { await fetchCities(); closeModal(); }
      } else {
        const res = await fetch(CITIES_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) { await fetchCities(); closeModal(); }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this city?")) return;
    try {
      const res = await fetch(`${CITIES_API}/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setCities((prev) => prev.filter((city) => city.id !== id));
      } else {
        alert(data.message || "Failed to delete city");
      }
    } catch (error) {
      console.log(error);
      alert("Something went wrong");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ name: "", state: "", cover: null });
    setPreview(null);
    setErrors({});
    setEditId(null);
  };

  return (
    <>
      <div className="c-tab-header">
        <p className="c-page-subtitle">{cities.length} {cities.length === 1 ? "city" : "cities"} listed</p>
        <button className="c-btn-primary" onClick={openAdd}>+ Add City</button>
      </div>

      {cities.length === 0 ? (
        <div className="c-empty">
          <p>No cities added yet.</p>
          <button className="c-btn-primary" style={{ marginTop: 14 }} onClick={openAdd}>+ Add City</button>
        </div>
      ) : (
        <div className="c-grid">
          {cities.map((city) => (
            <div className="c-card" key={city.id}>
              <div className="c-card-img-wrap">
                <img src={city.cover} alt={city.name} className="c-card-img" />
                <span className="c-card-badge">{city.state.toUpperCase()}</span>
              </div>
              <div className="c-card-body">
                <div className="c-card-info">
                  <span className="c-card-name">{city.name}</span>
                  <span className="c-card-state">{city.state}</span>
                </div>
                <div className="c-card-divider" />
                <div className="c-card-actions">
                  <button className="c-act-edit" onClick={() => openEdit(city)}>
                    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button className="c-act-del" onClick={() => handleDelete(city.id)}>
                    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="c-backdrop" onClick={closeModal}>
          <div className="c-modal" onClick={(e) => e.stopPropagation()}>
            <div className="c-modal-header">
              <h2 className="c-modal-title">{editId ? "Edit City" : "Add City"}</h2>
              <button className="c-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="c-modal-body">
              <div className="c-field">
                <label className="c-label">City Name</label>
                <input
                  className={`c-input${errors.name ? " c-input-err" : ""}`}
                  name="name"
                  value={form.name}
                  onChange={handleInput}
                  placeholder="e.g. Hyderabad"
                  autoFocus
                />
                {errors.name && <span className="c-err">{errors.name}</span>}
              </div>
              <div className="c-field">
                <label className="c-label">State</label>
                <input
                  className={`c-input${errors.state ? " c-input-err" : ""}`}
                  name="state"
                  value={form.state}
                  onChange={handleInput}
                  placeholder="e.g. Telangana"
                />
                {errors.state && <span className="c-err">{errors.state}</span>}
              </div>
              <div className="c-field">
                <label className="c-label">Cover Image</label>
                <div
                  className={`c-dropzone${dragging ? " c-dz-over" : ""}${errors.cover ? " c-dz-err" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current.click()}
                >
                  {preview ? (
                    <img src={preview} alt="preview" className="c-dz-preview" />
                  ) : (
                    <div className="c-dz-placeholder">
                      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#0a2ea0" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="c-dz-text">Click or drag to upload</span>
                      <span className="c-dz-hint">PNG, JPG, WEBP supported</span>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                    onChange={(e) => handleFile(e.target.files[0])} />
                </div>
                {preview && (
                  <button className="c-remove-img" onClick={() => { setPreview(null); setForm((f) => ({ ...f, cover: null })); }}>
                    Remove image
                  </button>
                )}
                {errors.cover && <span className="c-err">{errors.cover}</span>}
              </div>
            </div>
            <div className="c-modal-footer">
              <button className="c-btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="c-btn-primary" onClick={handleSubmit}>
                {editId ? "Save Changes" : "Add City"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── States Tab ──────────────────────────────────────────────────────────────

function StatesTab() {
  const [states, setStates] = useState([]);
  const [form, setForm] = useState({ name: "", state_code: "" });
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState(null);

  const fetchStates = async () => {
    try {
      const res = await fetch(STATES_API);
      const data = await res.json();
      if (data.success) setStates(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => { fetchStates(); }, []);

  const handleInput = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((err) => ({ ...err, [e.target.name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "State name is required.";
    if (!form.state_code.trim()) e.state_code = "State code is required.";
    return e;
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ name: "", state_code: "" });
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (state) => {
    setEditId(state.id);
    setForm({ name: state.name, state_code: state.state_code });
    setErrors({});
    setShowModal(true);
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    const payload = { name: form.name.trim(), state_code: form.state_code.trim().toUpperCase() };

    try {
      if (editId) {
        const res = await fetch(`${STATES_API}/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) { await fetchStates(); closeModal(); }
      } else {
        const res = await fetch(STATES_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (data.success) { await fetchStates(); closeModal(); }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this state?")) return;
    try {
      const res = await fetch(`${STATES_API}/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) {
        setStates((prev) => prev.filter((s) => s.id !== id));
      } else {
        alert(data.message || "Failed to delete state");
      }
    } catch (error) {
      console.log(error);
      alert("Something went wrong");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ name: "", state_code: "" });
    setErrors({});
    setEditId(null);
  };

  return (
    <>
      <div className="c-tab-header">
        <p className="c-page-subtitle">{states.length} {states.length === 1 ? "state" : "states"} listed</p>
        <button className="c-btn-primary" onClick={openAdd}>+ Add State</button>
      </div>

      {states.length === 0 ? (
        <div className="c-empty">
          <p>No states added yet.</p>
          <button className="c-btn-primary" style={{ marginTop: 14 }} onClick={openAdd}>+ Add State</button>
        </div>
      ) : (
        <div className="c-grid">
          {states.map((state) => (
            <div className="c-card c-state-card" key={state.id}>
              <div className="c-state-card-top">
                <span className="c-state-code-badge">{state.state_code}</span>
              </div>
              <div className="c-card-body">
                <div className="c-card-info">
                  <span className="c-card-name">{state.name}</span>
                  <span className="c-card-state">{state.state_code}</span>
                </div>
                <div className="c-card-divider" />
                <div className="c-card-actions">
                  <button className="c-act-edit" onClick={() => openEdit(state)}>
                    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button className="c-act-del" onClick={() => handleDelete(state.id)}>
                    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="c-backdrop" onClick={closeModal}>
          <div className="c-modal" onClick={(e) => e.stopPropagation()}>
            <div className="c-modal-header">
              <h2 className="c-modal-title">{editId ? "Edit State" : "Add State"}</h2>
              <button className="c-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="c-modal-body">
              <div className="c-field">
                <label className="c-label">State Name</label>
                <input
                  className={`c-input${errors.name ? " c-input-err" : ""}`}
                  name="name"
                  value={form.name}
                  onChange={handleInput}
                  placeholder="e.g. Maharashtra"
                  autoFocus
                />
                {errors.name && <span className="c-err">{errors.name}</span>}
              </div>
              <div className="c-field">
                <label className="c-label">State Code</label>
                <input
                  className={`c-input${errors.state_code ? " c-input-err" : ""}`}
                  name="state_code"
                  value={form.state_code}
                  onChange={handleInput}
                  placeholder="e.g. MH"
                  maxLength={10}
                  style={{ textTransform: "uppercase" }}
                />
                {errors.state_code && <span className="c-err">{errors.state_code}</span>}
              </div>
            </div>
            <div className="c-modal-footer">
              <button className="c-btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="c-btn-primary" onClick={handleSubmit}>
                {editId ? "Save Changes" : "Add State"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Cities() {
  const [activeTab, setActiveTab] = useState("cities");

  return (
    <div className="c-page">
      {/* Page Header */}
      <div className="c-page-header">
        <div className="c-title-group">
          <div className="c-page-icon">
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h1 className="c-page-title">Locations</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="c-tabs">
        <button
          className={`c-tab-btn${activeTab === "cities" ? " c-tab-active" : ""}`}
          onClick={() => setActiveTab("cities")}
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Cities
        </button>
        <button
          className={`c-tab-btn${activeTab === "states" ? " c-tab-active" : ""}`}
          onClick={() => setActiveTab("states")}
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          States
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "cities" ? <CitiesTab /> : <StatesTab />}
    </div>
  );
}